import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useIntl } from 'react-intl';
import { AdditionalServicesFragmentFragmentDoc } from '@packages/gql/generated/shopping/AdditionalServicesFragmentFragmentDoc';
import type { DividerProps } from '@packages/shared/src/ui/Divider/Divider';
import type { HTMLAttributes, ReactElement } from 'react';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { additionalServicesMessages } from './messages';
import type { AdditionalInfoItemProps } from '../AdditionalInfoItem';
import { AdditionalInfoItem } from '../AdditionalInfoItem';
import { getValidInfoList } from '../getValidInfoList';

/* GraphQL */ `
  fragment AdditionalServicesFragment on AdditionalServicesInfo {
    infoList {
      serviceLayerId
      translationKey
      iconUrl
    }
  }
`;

export type AdditionalServicesProps = {
  data?: FragmentType<typeof AdditionalServicesFragmentFragmentDoc> | null;
  divider?: ReactElement<DividerProps>;
} & Pick<AdditionalInfoItemProps, 'dialogTitle'> &
  HTMLAttributes<HTMLDivElement>;

export const AdditionalServices = ({
  data,
  divider,
  dialogTitle,
  className,
  ...props
}: AdditionalServicesProps) => {
  const { formatMessage } = useIntl();
  if (!data) return null;

  const { infoList } = getFragmentData(AdditionalServicesFragmentFragmentDoc, data);

  if (infoList.length === 0) return null;

  const validInfoList = getValidInfoList(infoList, additionalServicesMessages);

  if (validInfoList.length === 0) return null;

  return (
    <>
      {divider}
      <div {...props} className={cn('flex flex-col flex-nowrap items-start gap-2', className)}>
        {validInfoList.map((item, index) => (
          <AdditionalInfoItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            icon={item.iconUrl}
            serviceLayerId={item.serviceLayerId}
            text={formatMessage(item.messageDescriptor, {
              bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
            })}
            dialogTitle={dialogTitle}
          />
        ))}
      </div>
    </>
  );
};
